import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function TipsSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        These strategies are drawn from what high-performing sales teams do differently. Incorporate
        even a few of these habits and you will notice the difference in your productivity and
        pipeline quality.
      </p>

      <section id="tips-speed" aria-labelledby="tips-speed-heading">
        <h3 id="tips-speed-heading" className="font-semibold mb-2">
          Speed Tips
        </h3>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Use Global Search constantly</span> — Press{' '}
            <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-muted border border-border rounded">
              Ctrl
            </kbd>{' '}
            +{' '}
            <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-muted border border-border rounded">
              K
            </kbd>{' '}
            to jump to any record in seconds. It is faster than clicking through menus.
          </li>
          <li>
            <span className="font-medium">Default to "My" view</span> — Keep the My/All toggle on
            "My" so you only see your assigned records. Switch to "All" when you need to.
          </li>
          <li>
            <span className="font-medium">Create tasks from record pages</span> — When you are on an
            account or lead detail page, create tasks directly from the Activity tab. The owner and
            linked record are filled in automatically.
          </li>
          <li>
            <span className="font-medium">Bulk actions for repetitive work</span> — Select multiple
            records and update them all at once instead of editing one by one.
          </li>
          <li>
            <span className="font-medium">Use column sorting</span> — Click column headers to sort
            by due date, amount, or status. This helps you prioritize without scrolling.
          </li>
        </ul>
      </section>
      <Separator />

      <section id="tips-data-quality" aria-labelledby="tips-data-quality-heading">
        <h3 id="tips-data-quality-heading" className="font-semibold mb-2">
          Keeping Data Clean
        </h3>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Pay attention to duplicate warnings</span> — When the
            system flags a potential duplicate during record creation, take a moment to check.
            Preventing duplicates is easier than merging them later.
          </li>
          <li>
            <span className="font-medium">Fill in key fields</span> — At minimum, capture email,
            phone, company, and lead source. These fields power search, duplicate detection, and
            reporting.
          </li>
          <li>
            <span className="font-medium">Use the completeness indicator</span> — The profile
            completeness bar on account and lead detail pages shows how much data is filled in. Aim
            for 80%+ on your active records.
          </li>
          <li>
            <span className="font-medium">Log interactions consistently</span> — A quick note or
            task after every call takes 30 seconds and saves you (and your teammates) from guessing
            what happened.
          </li>
        </ul>
      </section>
      <Separator />

      <section id="tips-pipeline" aria-labelledby="tips-pipeline-heading">
        <h3 id="tips-pipeline-heading" className="font-semibold mb-2">
          Pipeline Management
        </h3>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Review your pipeline weekly</span> — Open the Pipeline
            Report or your Dashboard to check for stale deals, overdue close dates, and bottlenecks.
          </li>
          <li>
            <span className="font-medium">Update stages promptly</span> — Move deals forward (or
            mark them lost) as soon as the situation changes. An accurate pipeline means better
            forecasts for the whole team.
          </li>
          <li>
            <span className="font-medium">Set realistic close dates</span> — Overoptimistic dates
            inflate your forecast and create pressure. Base close dates on actual buyer signals.
          </li>
          <li>
            <span className="font-medium">Track competitors</span> — Use the opportunity fields to
            note which competitors are in the running. This feeds into Win/Loss Analysis and helps
            the team learn.
          </li>
        </ul>
      </section>
      <Separator />

      <section id="tips-collaboration" aria-labelledby="tips-collaboration-heading">
        <h3 id="tips-collaboration-heading" className="font-semibold mb-2">
          Team Collaboration
        </h3>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Assign tasks clearly</span> — When creating a task for a
            teammate, include enough context in the description so they can act without having to
            ask questions.
          </li>
          <li>
            <span className="font-medium">Use real-time updates</span> — Grackle CRM pushes updates
            in real time. If a colleague updates a record you are viewing, the data refreshes
            automatically.
          </li>
          <li>
            <span className="font-medium">Add contact roles on opportunities</span> — Document who
            the decision-maker, champion, and evaluator are. This helps anyone on the team step in
            if needed.
          </li>
          <li>
            <span className="font-medium">Log notes on records, not in email</span> — Notes attached
            to CRM records are visible to the whole team. Email threads are not. Use the CRM as the
            shared source of truth.
          </li>
        </ul>
      </section>
      <Separator />

      <ProTip>
        <span className="font-medium">The 2-minute rule:</span> If updating a record takes less than
        2 minutes, do it right now. The longer you wait, the more details you forget — and the more
        your pipeline drifts from reality.
      </ProTip>
    </>
  )
}
